import { FC, memo } from "react";
import { cn } from "~/lib/utils";

const DOT_SIZE_PX = 8;
const GRID_COLUMNS = 8;
const MAX_DOTS = 64;

interface AnnotationIndicatorProps {
  /** Array of annotation colors (hex strings) to display as dots */
  colors: string[];
  /** Additional CSS class names */
  className?: string;
  /** Custom dot size in pixels (defaults to 8) */
  dotSize?: number;
  /** Custom gap between dots in pixels — use negative values for overlap (defaults to 1) */
  gap?: number;
}

/**
 * A grid of colored circle indicators (8x8px each) that shows
 * annotations on a page or section.
 * Positioned absolutely in the top-right corner of its parent.
 * Displays up to 64 dots in an 8-column grid.
 */
const AnnotationIndicatorComponent: FC<AnnotationIndicatorProps> = ({
  colors,
  className,
  dotSize = DOT_SIZE_PX,
  gap: gapPx = 1,
}) => {
  if (colors.length === 0) return null;

  const visibleColors = colors.slice(0, MAX_DOTS);
  const columns = Math.min(visibleColors.length, GRID_COLUMNS);
  const effectiveColumnSize = dotSize + gapPx;

  return (
    <div
      className={cn("absolute top-1 right-1 pointer-events-none", className)}
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, ${effectiveColumnSize}px)`,
        gap: 0,
      }}
      aria-hidden="true"
    >
      {visibleColors.map((color, index) => (
        <span
          key={index}
          className="rounded-full"
          style={{
            width: dotSize,
            height: dotSize,
            backgroundColor: color,
            boxShadow: "0 0 0 0.5px rgba(0, 0, 0, 0.3)",
          }}
        />
      ))}
    </div>
  );
};

export const AnnotationIndicator = memo(AnnotationIndicatorComponent);
AnnotationIndicator.displayName = "AnnotationIndicator";
